'use client';

import { useEffect, useRef, useState } from 'react';
import { trackEmerald } from '../analytics';
import { useFlowDispatch, useFlowState } from '../FlowProvider';
import { hapMicro } from '../haptics';
import { isCoarsePointerDevice } from '../pointerType';
import { useSphereHandle } from '../sphereContext';
import {
  CityGroup,
  FauxCaret,
  Field,
  FieldFrame,
  Ghost,
  Heading,
  Note,
  OutlineCta,
  PrimaryCta,
  Scene,
  SceneBottom,
  SceneSpacer,
  SceneTop,
  SubText,
} from '../styles';
import { useWeatherFlow } from '../weather/useWeatherFlow';

export type SkySceneProps = {
  active: boolean;
};

/** Act 3 — geolocate or type a city; every failure path names the next action. */
export const SkyScene = ({ active }: SkySceneProps) => {
  const dispatch = useFlowDispatch();
  const { userName } = useFlowState();
  const sphereRef = useSphereHandle();
  const cityInputRef = useRef<HTMLInputElement>(null);
  // Set at each entry point, read once on resolve — a categorical funnel prop.
  const skyMethodRef = useRef<'geo' | 'city' | 'skip'>('geo');
  const [shakeVisible, setShakeVisible] = useState(false);
  // Gates the decorative FauxCaret: true only after a genuine focus event.
  const [cityFocused, setCityFocused] = useState(false);
  const [hasCityText, setHasCityText] = useState(false);

  const {
    mode,
    note,
    noteKind,
    fetching,
    shakeToken,
    requestGeo,
    openCityEntry,
    submitCity,
    skip,
  } = useWeatherFlow((wx) => {
    trackEmerald('emerald_sky_resolved', { method: skyMethodRef.current });
    dispatch({ type: 'ENTER_INSIGHTS', wx });
  });

  useEffect(() => {
    if (mode !== 'city') return;
    setHasCityText(false);
    setCityFocused(false);
    // Non-touch only — auto-focusing a touchscreen field breaks the iOS soft
    // keyboard. See pointerType.ts.
    if (!isCoarsePointerDevice()) {
      cityInputRef.current?.focus({ preventScroll: true });
    }
  }, [mode]);

  useEffect(() => {
    if (shakeToken === 0) return;
    setShakeVisible(true);
    const id = window.setTimeout(() => setShakeVisible(false), 450);
    return () => window.clearTimeout(id);
  }, [shakeToken]);

  const submitCityValue = () => {
    const value = cityInputRef.current?.value.trim() ?? '';
    if (!value) return;
    skyMethodRef.current = 'city';
    submitCity(value);
  };

  return (
    <Scene $on={active} aria-hidden={!active}>
      <SceneTop>
        <Heading>
          {userName ? (
            <>
              Nice to meet you, {userName}.
              <br />
              May it read your sky?
            </>
          ) : (
            'May it read your sky?'
          )}
        </Heading>
        <SubText>
          Your coordinates become weather and light, then forgotten. Never
          stored.
        </SubText>
      </SceneTop>
      <SceneSpacer />
      <SceneBottom>
        {mode === 'buttons' ? (
          <>
            <PrimaryCta
              type="button"
              disabled={fetching}
              onClick={() => {
                skyMethodRef.current = 'geo';
                requestGeo();
              }}
            >
              READ MY SKY
            </PrimaryCta>
            <OutlineCta
              type="button"
              disabled={fetching}
              onClick={openCityEntry}
            >
              TYPE A CITY
            </OutlineCta>
          </>
        ) : (
          <CityGroup>
            <FieldFrame>
              <Field
                ref={cityInputRef}
                type="text"
                maxLength={40}
                placeholder="Enter your city"
                autoComplete="off"
                autoCapitalize="words"
                spellCheck={false}
                enterKeyHint="search"
                aria-label="City name"
                $shake={shakeVisible}
                onFocus={() => setCityFocused(true)}
                onInput={(e) => {
                  setHasCityText(e.currentTarget.value.trim().length > 0);
                  sphereRef.current?.pulse(0.15);
                  hapMicro();
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') submitCityValue();
                }}
              />
              {!cityFocused && <FauxCaret aria-hidden="true" />}
            </FieldFrame>
            <PrimaryCta
              type="button"
              disabled={fetching || !hasCityText}
              onClick={submitCityValue}
            >
              SEARCH
            </PrimaryCta>
          </CityGroup>
        )}
        <Note role="status" $busy={noteKind === 'busy'}>
          {note}
        </Note>
        <Ghost
          type="button"
          onClick={() => {
            skyMethodRef.current = 'skip';
            skip();
          }}
        >
          skip
        </Ghost>
      </SceneBottom>
    </Scene>
  );
};
